const COOKIE_MAX_AGE = 60 * 60 * 24 * 30; // 30 days (covers "Remember me"; per-role limits are enforced in session.ts)
// Browsers enforce a ~4096 byte limit per cookie. Use 3000 to leave room for
// the name, attributes, and encoding overhead.
const CHUNK_SIZE = 3000;

function writeCookie(name: string, value: string, maxAge = COOKIE_MAX_AGE): void {
  const secure = location.protocol === "https:" ? "; Secure" : "";
  document.cookie = `${name}=${encodeURIComponent(value)}; Max-Age=${maxAge}; Path=/; SameSite=Strict${secure}`;
}

function readCookie(name: string): string | null {
  const match = document.cookie
    .split("; ")
    .find((row: string) => row.startsWith(`${name}=`));
  return match ? decodeURIComponent(match.split("=")[1]) : null;
}

function eraseCookie(name: string): void {
  document.cookie = `${name}=; Max-Age=0; Path=/; SameSite=Strict`;
}

// Chunked storage adapter — splits values that exceed the cookie size limit
// across multiple cookies (key.0, key.1, …) so the full session JSON is
// always persisted regardless of size.
export const cookieStorage: Storage = {
  getItem(key: string): string | null {
    const chunkCount = readCookie(`${key}.chunks`);
    if (chunkCount) {
      const n = parseInt(chunkCount, 10);
      let value = "";
      for (let i = 0; i < n; i++) {
        const chunk = readCookie(`${key}.${i}`);
        if (chunk === null) return null;
        value += chunk;
      }
      return value;
    }
    return readCookie(key);
  },

  setItem(key: string, value: string): void {
    if (value.length <= CHUNK_SIZE) {
      writeCookie(key, value);
      const prevChunkCount = readCookie(`${key}.chunks`);
      if (prevChunkCount) {
        const n = parseInt(prevChunkCount, 10);
        for (let i = 0; i < n; i++) eraseCookie(`${key}.${i}`);
        eraseCookie(`${key}.chunks`);
      }
      return;
    }

    const chunks = Math.ceil(value.length / CHUNK_SIZE);
    const prevChunksRaw = readCookie(`${key}.chunks`);
    const prevN = prevChunksRaw ? parseInt(prevChunksRaw, 10) : 0;
    for (let i = 0; i < chunks; i++) {
      writeCookie(`${key}.${i}`, value.slice(i * CHUNK_SIZE, (i + 1) * CHUNK_SIZE));
    }
    writeCookie(`${key}.chunks`, String(chunks));
    eraseCookie(key);
    for (let i = chunks; i < prevN; i++) eraseCookie(`${key}.${i}`);
  },

  removeItem(key: string): void {
    const chunkCount = readCookie(`${key}.chunks`);
    if (chunkCount) {
      const n = parseInt(chunkCount, 10);
      for (let i = 0; i < n; i++) eraseCookie(`${key}.${i}`);
      eraseCookie(`${key}.chunks`);
    }
    eraseCookie(key);
  },

  clear: () => {},
  key: (_index: number) => null,
  length: 0,
};
